import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import type { PayoutProfile, UpsertPayoutProfile, Withdrawal } from '@/shared/api/types';

export const WITHDRAWALS_PAGE_SIZE = 10;

export const settlementKeys = {
  all: ['settlements'] as const,
  summary: () => [...settlementKeys.all, 'summary'] as const,
  statements: () => [...settlementKeys.all, 'statements'] as const,
  payoutProfile: () => [...settlementKeys.all, 'payout-profile'] as const,
  withdrawals: (page: number) => [...settlementKeys.all, 'withdrawals', { page }] as const,
  preview: (amount: number) => [...settlementKeys.all, 'preview', amount] as const,
};

export const settlementSummaryQueryOptions = () =>
  queryOptions({
    queryKey: settlementKeys.summary(),
    queryFn: ({ signal }) => unwrap(api.GET('/api/settlements/summary', { signal })),
  });

export const statementsQueryOptions = () =>
  queryOptions({
    queryKey: settlementKeys.statements(),
    queryFn: ({ signal }) => unwrap(api.GET('/api/settlements/statements', { signal })),
    staleTime: 5 * 60_000,
  });

/**
 * 정산 정보 — 미등록이면 null.
 * 서버는 null 을 빈 본문(200)으로 보내므로 JSON 파싱에 기대지 않고 글자로 받아 직접 해석한다.
 */
export const payoutProfileQueryOptions = () =>
  queryOptions({
    queryKey: settlementKeys.payoutProfile(),
    queryFn: async ({ signal }): Promise<PayoutProfile | null> => {
      const text = await unwrap(
        api.GET('/api/settlements/payout-profile', { parseAs: 'text', signal }),
      );
      return text ? (JSON.parse(text) as PayoutProfile | null) : null;
    },
  });

export const withdrawalsQueryOptions = (page: number) =>
  queryOptions({
    queryKey: settlementKeys.withdrawals(page),
    queryFn: ({ signal }) =>
      unwrap(
        api.GET('/api/settlements', {
          params: { query: { page, limit: WITHDRAWALS_PAGE_SIZE } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });

/** 출금 미리보기 — 금액이 바뀔 때마다 서버 규칙(소득 유형·세율)으로 다시 계산한다 */
export const withdrawalPreviewQueryOptions = (amount: number) =>
  queryOptions({
    queryKey: settlementKeys.preview(amount),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/settlements/preview', { params: { query: { amount } }, signal })),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

export const settlementApi = {
  upsertPayoutProfile: (body: UpsertPayoutProfile) =>
    unwrap(api.PUT('/api/settlements/payout-profile', { body })),

  requestWithdrawal: (amount: number): Promise<Withdrawal> =>
    unwrap(api.POST('/api/settlements', { body: { amount } })),
};
